<template>
  <div class="w-full h-full flex flex-col">
    <!-- 固定搜索區域 -->
    <div class="fixed-section sticky top-[109px] w-full bg-gray-50 z-20">
      <div class="review-search-form flex flex-col lg:flex-row">
        <div class="flex flex-1 flex-col lg:flex-row w-full lg:w-2/3 lg:mb-0 justify-between items-center">
          <div class="flex flex-col w-full">
            <div class="flex justify-between items-center w-full">
              <div class="relative">
                <div class="search-icon" @click="performSearch"></div>
                <search-input
                  v-model="state.currentSearch"
                  :placeholder="$t('models.model_search_form.model_title')"
                  @search="performSearch"
                />
              </div>
              <div class="flex justify-center">
                <button class="btn text-12px hover:text-gray-150" @click="abortSort">
                  {{ $t('models.index.cancel') }}
                </button>
                <button 
                  class="btn btn-primary flex h-8 py-1"
                  @click="submitSortOrder"
                  :disabled="state.isSaving">
                  <img src="../../images/icons/save_24.svg" class="w-6 h-6" />
                  <span class="ml-2 inline-block text-14px">
                    {{ state.isSaving ? 'Saving...' : $t('models.index.save_sort') }}
                  </span>
                </button>
              </div> 
            </div>
            
            <!-- 排序類型選擇 -->
            <div class='comment-order-type relative pr-2 mt-4'>
              <div class="icon brightness-50"></div>
              <template v-for="type in orderTypes" :key="type.value">
                <input 
                  type="radio"
                  name='comment-order-type'
                  class='hidden'
                  :id="'order_type_' + type.value"
                  :value="type.value"
                  :checked="state.orderType === type.value">
                <label 
                  class='text-grey-3 hover:text-grey-5'
                  :for="'order_type_' + type.value"
                  @click="updateOrderType(type.value)">
                  {{ $t(`models.index.${type.label}`) }}
                </label>
                <span class='text-grey-3' v-if="!type.isLast">|</span>
              </template>
              
              <!-- 說明面板 -->
              <div class="absolute right-0">
                <nav-panel :submitErrors="{title: 'Error Title', content: 'Error Content'}"
                           :panelClass="'w-[288px]'">
                  <template #icon>
                    <img src="../../images/circle_help.svg" class="w-5 h-5" alt="Warning" />
                  </template>
                  <template #content>
                    <div class="custom-content text-[12px]">
                      <p class="font-bold">{{ $t(`models.index.sort_defined`) }}</p>
                      <ul class="ml-4">
                        <template v-for="item in sortingCriteria" :key="item">
                          <li class="list-disc text-orange-10 mt-2">{{ $t(`models.index.${item}`) }}</li>
                          <p class="mb-2">{{ $t(`models.index.${item}_defined`) }}</p>
                        </template>
                      </ul>
                    </div>
                  </template>
                </nav-panel>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 表頭 -->
      <div class="mt-4 text-12px text-gray-200 font-bold grid gap-x-4" :class="gridHeaderClass">
        <div v-if="isSortable"></div>
        <div class="pl-2">{{ $t(`models.index.thumbnail`) }}</div>
        <div>{{ $t(`models.index.model_title`) }}</div> 
        <div>{{ `Pin (${pinnedItems.length}/8)` }}</div>
        <hr :class="isSortable ? 'col-span-4 border-grey-3 my-2' : 'col-span-3 border-grey-3 my-2'">
      </div>
    </div>

    <!-- 內容區域 -->
    <div class="flex flex-col">
      <!-- Loading -->
      <template v-if="state.initialLoading">
        <div class="flex flex-col items-center justify-center flex-1 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
          <p class="text-16px font-medium text-center">
            <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
          </p>
        </div>
      </template>

      <!-- No items -->
      <template v-else-if="state.allItems.length === 0 || state.isSearchActive">
        <div class="flex flex-col items-center justify-center flex-1 mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
          <img src="../../images/sad.svg" class="block w-12 h-12 mb-4" />
          <p class="text-16px font-medium text-center">
            <template v-if="state.isSearchActive">
              {{ $t('models.index.no_record_model') }}
            </template>
            <template v-else>
              {{ $t('models.index.no_model_now') }}
              <br>
              {{ $t('models.index.create_your_model') }}
            </template>
          </p>
        </div>
      </template>

      <!-- Items -->
      <template v-else>
        <!-- Pinned Items -->
        <div 
          v-if="pinnedItems.length > 0" 
          class="pin-model-container flex flex-col gap-y-2 my-2">
          <post-sort-item
            v-for="item in pinnedItems"
            :key="`pinned-${item.id}`"
            :data-model-id="item.id"
            source="ProfileSortModels"
            class="sorted-model-item"
            :item="item"
            :is-pinned="true"
            :show-pin-icon="true"
            :order-type="state.orderType"
            :is-sortable="isSortable"
            :index="item.sortOrder"
            @moveToTop="() => moveItem(item.id, 'top', 'pinned')"
            @moveToBottom="() => moveItem(item.id, 'bottom', 'pinned')"
            @moveToPin="() => togglePin(item.id)"
          />
        </div>

        <!-- 分隔線 -->
        <hr v-if="pinnedItems.length > 0" :class="isSortable ? 'col-span-4 border-grey-3 my-2' : 'hidden'">

        <!-- Normal Items -->
        <div class="model-container flex flex-col gap-y-2 flex-1 overflow-auto scrollbar pr-1 -mr-3">
          <post-sort-item
            v-for="item in normalItems"
            :key="`normal-${item.id}`"
            :data-model-id="item.id"
            source="ProfileSortModels"
            class="sorted-model-item"
            :item="item"
            :is-pinned="false"
            :show-pin-icon="pinnedItems.length < 8"
            :order-type="state.orderType"
            :is-sortable="isSortable"
            :pin-count="pinnedItems.length"
            :index="item.sortOrder"
            @moveToTop="() => moveItem(item.id, 'top', 'normal')"
            @moveToBottom="() => moveItem(item.id, 'bottom', 'normal')"
            @moveToPin="() => togglePin(item.id)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import Sortable from 'sortablejs';

export default defineComponent({
  setup() {
    const sortingCriteria = ['pv', 'published_date', 'price', 'custom_order'];

    const state = reactive({
      currentUser: null,
      orderType: 'published_date',
      isSaving: false,
      hasCustomOrder: false,
      hasVerifiedItems: false,
      allItems: [],
      sortedItems: [],
      totalItemCount: 0,
      initialLoading: true,
      currentSearch: '',
      filteredResults: [],
      isSearchActive: false
    });

    const sortableInstances = ref({
      normal: null,
      pinned: null,
    });

    // 顯示項目計算
    const currentDisplayItems = computed(() => {
      if (state.filteredResults.length > 0) {
        // 搜索時：將搜索結果與排序狀態合併
        return state.filteredResults.map(item => {
          const sortedItem = state.sortedItems.find(si => si.id === item.id);
          return sortedItem 
            ? { ...item, ...sortedItem }  // 合併原始數據和排序狀態
            : { ...item, isPinned: false, sortOrder: 0 }; // 預設狀態
        });
      }
      // 非搜索時：直接使用排序項目
      return state.sortedItems;
    });

    // 顯示項目計算屬性
    const pinnedItems = computed(() => 
      currentDisplayItems.value
        .filter(item => item.isPinned)
        .sort((a, b) => a.sortOrder - b.sortOrder)
    );

    const normalItems = computed(() => 
      currentDisplayItems.value
        .filter(item => !item.isPinned)
        .sort((a, b) => a.sortOrder - b.sortOrder)
    );

    const isSortable = computed(() => {
      return (
        state.orderType === 'custom_order' &&
        state.totalItemCount === state.sortedItems.length
      );
    });

    const orderTypes = computed(() => {
      const types = [
        { value: 'published_date', label: 'published_date' },
        { value: 'price', label: 'price' },
        { value: 'custom_order', label: 'custom_order' }
      ];

      if (state.hasVerifiedItems) {
        types.unshift({ value: 'pv', label: 'pv' });
      }
      types[types.length - 1].isLast = true;
      return types;
    });

    const gridHeaderClass = computed(() => {
      return isSortable.value
        ? 'grid-cols-[58px_68px_38fr_80px]'
        : 'grid-cols-[64px_38fr_80px]';
    });

    //  Pin 操作
    const togglePin = (id) => {
      const item = state.sortedItems.find(item => item.id === id);
      if (!item) return;

      // 檢查置頂限制
      if (!item.isPinned && pinnedItems.value.length >= 8) {
        alert('A maximum of 8 items can be pinned');
        return;
      }

      item.isPinned = !item.isPinned;
      recalculateSortOrder();
    };

    // 修正移動邏輯，只在對應範圍內移動
    const moveItem = (id, direction, scope) => {    
      const item = state.sortedItems.find(item => item.id === id);
      if (!item) return;
      
      if (scope === 'pinned') {
        // 只在 pinned 範圍內移動
        const currentPinnedItems = pinnedItems.value;
        const currentIndex = currentPinnedItems.findIndex(p => p.id === id);
        
        if (currentIndex === -1) return;
        
        let newIndex;
        switch(direction) {
          case 'top':
            newIndex = 0;
            break;
          case 'bottom':
            newIndex = currentPinnedItems.length - 1;
            break;
          default:
            return;
        }
        
        if (currentIndex === newIndex) return;
        
        // 重新排序 pinned 項目
        reorderPinnedItems(currentIndex, newIndex);
        
      } else if (scope === 'normal') {
        // 只在 normal 範圍內移動
        const currentNormalItems = normalItems.value;
        const currentIndex = currentNormalItems.findIndex(n => n.id === id);
        
        if (currentIndex === -1) return;
        
        let newIndex;
        switch(direction) {
          case 'top':
            newIndex = 0;
            break;
          case 'bottom':
            newIndex = currentNormalItems.length - 1;
            break;
          default:
            return;
        }
        
        if (currentIndex === newIndex) return;
        
        // 重新排序 normal 項目
        reorderNormalItems(currentIndex, newIndex);
      }
    };

    // 獨立的 pinned 項目重排序函數
    const reorderPinnedItems = (fromIndex, toIndex) => {
      const currentPinned = [...pinnedItems.value];
      
      // 在陣列中移動項目
      const [movedItem] = currentPinned.splice(fromIndex, 1);
      currentPinned.splice(toIndex, 0, movedItem);
      
      // 更新 sortOrder，pinned 項目從 0 開始
      currentPinned.forEach((item, index) => {
        const originalItem = state.sortedItems.find(si => si.id === item.id);
        if (originalItem) {
          originalItem.sortOrder = index;
        }
      });
    };

    // 獨立的 normal 項目重排序函數
    const reorderNormalItems = (fromIndex, toIndex) => {
      const currentNormal = [...normalItems.value];
      const pinnedCount = pinnedItems.value.length;
      
      // 在陣列中移動項目
      const [movedItem] = currentNormal.splice(fromIndex, 1);
      currentNormal.splice(toIndex, 0, movedItem);
      
      // 更新 sortOrder，normal 項目需要加上 pinnedCount 偏移
      currentNormal.forEach((item, index) => {
        const originalItem = state.sortedItems.find(si => si.id === item.id);
        if (originalItem) {
          originalItem.sortOrder = pinnedCount + index;
        }
      });
    };

    // 統一的排序重計算函數（用於 togglePin）
    const recalculateSortOrder = () => {
      // 重新計算 pinned 項目的 sortOrder（從 0 開始）
      const pinned = state.sortedItems.filter(item => item.isPinned);
      pinned.forEach((item, index) => {
        item.sortOrder = index;
      });
      
      // 重新計算 normal 項目的 sortOrder（從 pinnedCount 開始）
      const normal = state.sortedItems.filter(item => !item.isPinned);
      normal.forEach((item, index) => {
        item.sortOrder = pinned.length + index;
      });
    };

    const applySorting = (items, sortType) => {
      const sortedItems = [...items];
      
      switch (sortType) {
        case 'custom_order':
          // custom_order 使用 sortOrder 排序
          return sortedItems.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        case 'price':
          return sortedItems.sort((a, b) => (b.price || 0) - (a.price || 0));
        case 'published_date':
          return sortedItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        case 'pv':
          return sortedItems.sort((a, b) => {
            if (a.phrozen_verified && !b.phrozen_verified) return -1;
            if (!a.phrozen_verified && b.phrozen_verified) return 1;
            return new Date(b.created_at) - new Date(a.created_at);
          });
        default:
          return sortedItems;
      }
    };

    const performSearch = async () => {
      if (!state.currentSearch.trim()) {
        state.filteredResults = [];
        state.isSearchActive = false;
      } else {
        const keyword = state.currentSearch.toLowerCase().trim();
        state.filteredResults = state.allItems.filter(item => 
          item.title.toLowerCase().includes(keyword)
        );
        
        await nextTick(() => {
          state.isSearchActive = state.filteredResults.length < 1;
        });
      }
    };

    const updateOrderType = (type) => {
      if (state.orderType === type) return;
      state.orderType = type;
      
      if (type !== 'custom_order') {
        const allSorted = applySorting([...state.sortedItems], type);
        allSorted.forEach((item, index) => {
          const originalItem = state.sortedItems.find(si => si.id === item.id);
          if (originalItem) {
            originalItem.sortOrder = index;
          }
        });
      } else {
        recalculateCustomOrderIndex();
      }
    };

    const recalculateCustomOrderIndex = () => {
      // 獲取當前 pinned 項目並重新編號
      const currentPinned = pinnedItems.value;
      currentPinned.forEach((item, index) => {
        const originalItem = state.sortedItems.find(si => si.id === item.id);
        if (originalItem) {
          originalItem.sortOrder = index;
        }
      });
      
      // 獲取當前 normal 項目並重新編號
      const currentNormal = normalItems.value;
      currentNormal.forEach((item, index) => {
        const originalItem = state.sortedItems.find(si => si.id === item.id);
        if (originalItem) {
          originalItem.sortOrder = currentPinned.length + index;
        }
      });
    };

    // API
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch('/api/v1/profile/current_user', {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          state.currentUser = await response.json();
        } else {
          throw new Error('Failed to fetch current user');
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    const fetchSortedModels = async () => {
      try {
        const response = await fetch('/api/v1/profile/sorted_models');
        if (response.ok) {
          const { normal, pinned } = await response.json();
          return {
            normalIds: (normal || []).map(id => Number(id)),
            pinnedIds: (pinned || []).map(id => Number(id))
          };
        }
      } catch (error) {
        console.error('Error fetching sorted data:', error);
        return { normalIds: [], pinnedIds: [] };
      }
    };

    const fetchAllModels = async () => {
      const url = `/api/v1/profile/all_models`;
      const params = new URLSearchParams({
        kw: state.currentSearch,
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          return await response.json();
        }
      } catch (error) {
        console.error('Error fetching model data:', error);
        return [];
      }
    };

    // 統一排序項目數據結構
    const buildSortedItems = (items, normalIds, pinnedIds) => {
      const pinnedSet = new Set(pinnedIds);
      const allOrderIds = [...pinnedIds, ...normalIds];
      
      return items.map(item => {
        const isPinned = pinnedSet.has(item.id);
        const orderIndex = allOrderIds.indexOf(item.id);
        
        return {
          ...item,
          isPinned,
          sortOrder: orderIndex >= 0 ? orderIndex : items.length 
        };
      }).sort((a, b) => a.sortOrder - b.sortOrder);
    };

    const submitSortOrder = async () => {
      if (state.isSaving) return;
      state.isSaving = true;

      try {
          // 直接從計算屬性獲取排序結果
        const pinnedIds = pinnedItems.value.map(item => item.id);
        const normalIds = normalItems.value.map(item => item.id);

        const response = await fetch('/api/v1/profile/sorted_models', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({
            pinned_order: pinnedIds,
            normal_order: normalIds
          })
        });

        if (response.ok) {
          window.location.href = '/profile/models/sorting';
        } else {
          throw new Error('Failed to save sort order');
        }
      } catch (error) {
        console.error('Error saving sort order:', error);
        alert('Failed to save sort order. Please try again.');
      } finally {
        state.isSaving = false;
      }
    };

    const abortSort = () => {
      window.location.href = '/profile/models';
    };

    // 拖拽功能
    const initSortable = () => {
      initPinnedSortable();
      initNormalSortable();
    };

    const initPinnedSortable = () => {
      const pinnedContainer = document.querySelector('.pin-model-container');
      if (pinnedContainer && !sortableInstances.value.pinned) {
        sortableInstances.value.pinned = Sortable.create(pinnedContainer, {
          animation: 150,
          group: 'pinModels',
          disabled: !isSortable.value,
          onEnd: () => syncOrderFromDOM('pinned')
        });
      }
    };

    const initNormalSortable = () => {
      const normalContainer = document.querySelector('.model-container');
      if (normalContainer && !sortableInstances.value.normal) {
        sortableInstances.value.normal = Sortable.create(normalContainer, {
          animation: 150,
          group: 'normalModels',
          disabled: !isSortable.value,
          onEnd: () => syncOrderFromDOM('normal')
        });
      }
    };

    // 拖拽後的排序同步
    const syncOrderFromDOM = (type) => {
      const containerClass = type === 'normal' ? '.model-container' : '.pin-model-container';
      const newOrder = Array.from(document.querySelectorAll(`${containerClass} > div`))
        .map(el => Number(el.dataset.modelId));
      
      const targetItems = type === 'pinned' ? pinnedItems.value : normalItems.value;
      newOrder.forEach((id, index) => {
        const item = targetItems.find(item => item.id === id);
        if (item) {
          // 計算 sortOrder 偏移
          item.sortOrder = type === 'pinned' ? index : pinnedItems.value.length + index;
        }
      });
    };

    const updateSortableOptions = (newSortable) => {
      if (sortableInstances.value.normal) {
        sortableInstances.value.normal.option('disabled', !newSortable);
      }
      if (sortableInstances.value.pinned) {
        sortableInstances.value.pinned.option('disabled', !newSortable);
      }
    };

    // 監聽器
    watch(isSortable, updateSortableOptions);

    onMounted(async () => {
      try {
        await fetchCurrentUser();
        if (!state.currentUser) {
          console.error('Failed to fetch current user');
          return;
        }

        const [items, { normalIds, pinnedIds }] = await Promise.all([
          fetchAllModels(),
          fetchSortedModels()
        ]);

        state.allItems = items;
        state.sortedItems = buildSortedItems(items, normalIds, pinnedIds);
        state.totalItemCount = items.length;
        state.hasCustomOrder = normalIds.length > 0 || pinnedIds.length > 0;
        state.hasVerifiedItems = items.some(item => item.phrozen_verified);
        
        // 預設排序類型
        state.orderType = state.hasCustomOrder 
          ? 'custom_order'
          : state.hasVerifiedItems ? 'pv' : 'published_date';

        setTimeout(() => {
          initSortable();
        }, 100);
      } catch (error) {
        console.error('Error in onMounted:', error);
      } finally {
        state.initialLoading = false;
      }
    });

    onUnmounted(() => {
      if (sortableInstances.value.normal) {
        sortableInstances.value.normal.destroy();
      }
      if (sortableInstances.value.pinned) {
        sortableInstances.value.pinned.destroy();
      }
    });

    return {
      sortingCriteria,
      state,
      orderTypes,
      pinnedItems,
      normalItems,
      isSortable,
      gridHeaderClass,
      togglePin,
      moveItem,
      performSearch,
      updateOrderType,
      submitSortOrder,
      abortSort,
    };
  }
});
</script>